/**
 * Find/Replace utility for markdown content
 * Works on entire markdown string instead of sections
 */

export interface MarkdownFindReplacePreview {
  lineNumber: number;
  original: string;
  replaced: string;
  originalParts?: Array<{ text: string; isMatch: boolean }>;
  replacedParts?: Array<{ text: string; isMatch: boolean }>;
}

const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Performs find/replace on markdown content and generates previews
 * @param markdown - The markdown content to search/replace in
 * @param find - Text to find
 * @param replace - Text to replace with
 * @param caseSensitive - Whether search should be case sensitive
 * @param maxPreviews - Maximum number of preview lines to generate
 * @returns Object with updated markdown and preview array
 */
export const findReplaceInMarkdown = (
  markdown: string,
  find: string,
  replace: string,
  caseSensitive = false,
  maxPreviews = 100,
): { updatedMarkdown: string; previews: MarkdownFindReplacePreview[] } => {
  if (!find) {
    return { updatedMarkdown: markdown, previews: [] };
  }

  const flags = caseSensitive ? 'g' : 'gi';
  const previews: MarkdownFindReplacePreview[] = [];
  let previewCount = 0;

  // Split markdown into lines for processing
  const lines = markdown.split('\n');
  const updatedLines: string[] = [];

  lines.forEach((line, lineIndex) => {
    // Create a fresh regex for each line to reset lastIndex
    const lineRegex = new RegExp(escapeRegExp(find), flags);
    const replacedLine = line.replace(lineRegex, replace);

    // If line was changed and we haven't hit preview limit, generate preview
    if (replacedLine !== line && previewCount < maxPreviews) {
      // Use matchAll for cleaner iteration (ES2020)
      const matches = Array.from(line.matchAll(new RegExp(escapeRegExp(find), flags)));

      if (matches.length > 0) {
        // Build original parts
        const originalParts: Array<{ text: string; isMatch: boolean }> = [];
        let lastIndex = 0;

        for (const match of matches) {
          const matchIndex = match.index;
          if (matchIndex !== undefined && matchIndex > lastIndex) {
            originalParts.push({ text: line.substring(lastIndex, matchIndex), isMatch: false });
          }
          if (matchIndex !== undefined) {
            originalParts.push({ text: match[0], isMatch: true });
            lastIndex = matchIndex + match[0].length;
          }
        }
        if (lastIndex < line.length) {
          originalParts.push({ text: line.substring(lastIndex), isMatch: false });
        }

        // Build replaced parts (reuse match positions)
        const replacedParts: Array<{ text: string; isMatch: boolean }> = [];
        lastIndex = 0;

        for (const match of matches) {
          const matchIndex = match.index;
          if (matchIndex !== undefined && matchIndex > lastIndex) {
            replacedParts.push({ text: line.substring(lastIndex, matchIndex), isMatch: false });
          }
          if (matchIndex !== undefined) {
            replacedParts.push({ text: replace, isMatch: true });
            lastIndex = matchIndex + match[0].length;
          }
        }
        if (lastIndex < line.length) {
          replacedParts.push({ text: line.substring(lastIndex), isMatch: false });
        }

        previews.push({
          lineNumber: lineIndex + 1, // 1-indexed for display
          original: line,
          replaced: replacedLine,
          originalParts,
          replacedParts,
        });
        previewCount++;
      }
    }

    updatedLines.push(replacedLine);
  });

  const updatedMarkdown = updatedLines.join('\n');

  return { updatedMarkdown, previews };
};

export default findReplaceInMarkdown;
